/**
 * @namespace com.ang.portal.service
 */

export interface UserInfo {
	id: number;
	email: string;
	name: string;
	company_name?: string;
	roles: string[];
	permissions: string[];
	plugins: string[];
	groups?: string[];
	is_active?: boolean;
	/** True for the system admin seeded at startup, which alone may delete a system
	 * admin. Absent on sessions established before the backend started sending it. */
	isRootAdmin?: boolean;
	/** Preferred UI language ("de" | "en"). Absent on sessions established before
	 * the backend started sending it, in which case the local choice stands. */
	language?: string;
}

export interface LoginResponse {
	accessToken: string;
	refreshToken: string;
	user: UserInfo;
}

export interface Requires2FAResponse {
	requires2FA: true;
	tempToken: string;
}

/** Login answer when 2FA is mandatory and the user has none yet: no session until
 * an authenticator is enrolled with the setup token. */
export interface Requires2FASetupResponse {
	requires2FASetup: true;
	setupToken: string;
}

/** The enrolment step that opens the session; backup codes are shown once. */
export interface Enrol2FAResponse extends LoginResponse {
	backupCodes: string[];
}

export interface Setup2FAResponse {
	qrCode: string;
	secret: string;
}

export interface TwoFAStatusResponse {
	enabled: boolean;
	backupCodesRemaining: number;
	/** Mandatory by company policy - the user cannot turn it off. */
	required?: boolean;
}

/** An auth failure with the backend's machine-readable reason, e.g.
 * `password_login_disabled`. */
export class AuthError extends Error {
	readonly code?: string;

	constructor(message: string, code?: string) {
		super(message);
		this.code = code;
	}
}

export interface Enable2FAResponse {
	enabled: boolean;
	backupCodes: string[];
	message: string;
}

export interface ForgotPasswordResponse {
	message: string;
	debugResetUrl?: string;
}

export interface ResetPasswordResponse {
	message: string;
}

export type LoginResult =
	| LoginResponse
	| Requires2FAResponse
	| Requires2FASetupResponse;

export function isRequires2FA(
	result: LoginResult,
): result is Requires2FAResponse {
	return "requires2FA" in result && result.requires2FA === true;
}

export function isRequires2FASetup(
	result: LoginResult,
): result is Requires2FASetupResponse {
	return "requires2FASetup" in result && result.requires2FASetup === true;
}

const STORAGE_KEYS = {
	ACCESS_TOKEN: "portal_access_token",
	REFRESH_TOKEN: "portal_refresh_token",
	USER_INFO: "portal_user_info",
} as const;

export const SSO_ERROR_STORAGE_KEY = "portal_sso_error";

let refreshPromise: Promise<LoginResponse> | null = null;

const AuthService = {
	async login(email: string, password: string): Promise<LoginResult> {
		const response = await fetch("/api/auth/login", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ email, password }),
		});

		if (!response.ok) {
			const errorData = (await response.json().catch(() => ({}))) as {
				message?: string;
				code?: string;
			};
			throw new AuthError(errorData.message || "Login failed", errorData.code);
		}

		const data = (await response.json()) as LoginResult;

		// Only store tokens for full login (not 2FA pending or enrolment pending)
		if (!isRequires2FA(data) && !isRequires2FASetup(data)) {
			this.storeTokens(data);
		}

		return data;
	},

	/** Forced enrolment, step 1: QR code and secret for the authenticator app. */
	async startEnrol2FA(setupToken: string): Promise<Setup2FAResponse> {
		const response = await fetch("/api/auth/2fa/enrol/start", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ setupToken }),
		});

		if (!response.ok) {
			const errorData = (await response.json().catch(() => ({}))) as {
				message?: string;
			};
			throw new Error(errorData.message || "2FA setup failed");
		}

		return (await response.json()) as Setup2FAResponse;
	},

	/** Forced enrolment, step 2: a valid code enables 2FA and opens the session. */
	async completeEnrol2FA(
		setupToken: string,
		code: string,
	): Promise<Enrol2FAResponse> {
		const response = await fetch("/api/auth/2fa/enrol/complete", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ setupToken, code }),
		});

		if (!response.ok) {
			const errorData = (await response.json().catch(() => ({}))) as {
				message?: string;
			};
			throw new Error(errorData.message || "2FA setup failed");
		}

		const data = (await response.json()) as Enrol2FAResponse;
		this.storeTokens(data);
		return data;
	},

	loginWithMicrosoft(): void {
		window.location.href = "/api/auth/sso/microsoft/start";
	},

	async exchangeSsoToken(token: string): Promise<LoginResponse> {
		const response = await fetch("/api/auth/sso/microsoft/exchange", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ token }),
		});

		if (!response.ok) {
			const errorData = (await response.json().catch(() => ({}))) as {
				message?: string;
			};
			throw new Error(errorData.message || "Microsoft sign-in failed");
		}

		const data = (await response.json()) as LoginResponse;
		this.storeTokens(data);
		return data;
	},

	async verify2FA(tempToken: string, code: string): Promise<LoginResponse> {
		const response = await fetch("/api/auth/2fa/validate", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ tempToken, code }),
		});

		if (!response.ok) {
			const errorData = (await response.json().catch(() => ({}))) as {
				message?: string;
			};
			throw new Error(errorData.message || "2FA verification failed");
		}

		const data = (await response.json()) as LoginResponse;
		this.storeTokens(data);
		return data;
	},

	async get2FAStatus(): Promise<TwoFAStatusResponse> {
		const response = await this.authenticatedFetch("/api/auth/2fa/status");
		if (!response.ok) {
			throw new Error("Failed to fetch 2FA status");
		}
		return (await response.json()) as TwoFAStatusResponse;
	},

	async setup2FAAuthenticated(): Promise<Setup2FAResponse> {
		const response = await this.authenticatedFetch("/api/auth/2fa/setup", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
		});
		if (!response.ok) {
			const errorData = (await response.json().catch(() => ({}))) as { message?: string };
			throw new Error(errorData.message || "2FA setup failed");
		}
		return (await response.json()) as Setup2FAResponse;
	},

	async enable2FA(code: string): Promise<Enable2FAResponse> {
		const response = await this.authenticatedFetch("/api/auth/2fa/enable", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ code }),
		});
		if (!response.ok) {
			const errorData = (await response.json().catch(() => ({}))) as { message?: string };
			throw new Error(errorData.message || "2FA enable failed");
		}
		return (await response.json()) as Enable2FAResponse;
	},

	async disable2FA(password: string, code: string): Promise<void> {
		const response = await this.authenticatedFetch("/api/auth/2fa/disable", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ password, code }),
		});
		if (!response.ok) {
			const errorData = (await response.json().catch(() => ({}))) as { message?: string };
			throw new Error(errorData.message || "Failed to disable 2FA");
		}
	},

	async regenerateBackupCodes(password: string): Promise<{ backupCodes: string[] }> {
		const response = await this.authenticatedFetch("/api/auth/2fa/backup-codes", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ password }),
		});
		if (!response.ok) {
			const errorData = (await response.json().catch(() => ({}))) as { message?: string };
			throw new Error(errorData.message || "Failed to regenerate backup codes");
		}
		return (await response.json()) as { backupCodes: string[] };
	},

	async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
		const response = await fetch("/api/auth/forgot-password", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ email }),
		});

		if (!response.ok) {
			const errorData = (await response.json().catch(() => ({}))) as {
				message?: string;
			};
			throw new Error(errorData.message || "Forgot password failed");
		}

		return (await response.json()) as ForgotPasswordResponse;
	},

	async resetPassword(
		token: string,
		newPassword: string,
	): Promise<ResetPasswordResponse> {
		const response = await fetch("/api/auth/reset-password", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ token, newPassword }),
		});

		if (!response.ok) {
			const errorData = (await response.json().catch(() => ({}))) as {
				message?: string;
			};
			throw new Error(errorData.message || "Reset password failed");
		}

		return (await response.json()) as ResetPasswordResponse;
	},

	logout(): void {
		const refreshToken = this.getRefreshToken();

		if (refreshToken) {
			const accessToken = this.getAccessToken();
			const headers: Record<string, string> = {
				"Content-Type": "application/json",
			};
			if (accessToken) {
				headers["Authorization"] = `Bearer ${accessToken}`;
			}

			// Fire-and-forget: best-effort server-side revocation
			fetch("/api/auth/logout", {
				method: "POST",
				headers,
				body: JSON.stringify({ refreshToken }),
			}).catch(() => {
				/* ignore network errors during logout */
			});
		}

		this.clearLocalSession();
	},

	/**
	 * Forget this tab's session without revoking it on the server. For a fresh
	 * sign-in another application asked for: a pop-up may hold a copy of the
	 * opener's tokens, and revoking them would sign the opener out too.
	 */
	clearLocalSession(): void {
		sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
		sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
		sessionStorage.removeItem(STORAGE_KEYS.USER_INFO);
	},

	isAuthenticated(): boolean {
		return !!sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
	},

	getAccessToken(): string | null {
		return sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
	},

	getRefreshToken(): string | null {
		return sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
	},

	getUserInfo(): UserInfo | null {
		const raw = sessionStorage.getItem(STORAGE_KEYS.USER_INFO);
		if (!raw) {
			return null;
		}
		return JSON.parse(raw) as UserInfo;
	},

	storeTokens(data: LoginResponse): void {
		sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
		sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
		sessionStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(data.user));
	},

	updateStoredUser(user: UserInfo): void {
		sessionStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(user));
	},

	async refreshTokens(): Promise<LoginResponse> {
		// Deduplicate concurrent refresh attempts
		if (refreshPromise !== null) {
			return refreshPromise;
		}

		const refreshToken = this.getRefreshToken();
		if (!refreshToken) {
			throw new Error("No refresh token available");
		}

		refreshPromise = fetch("/api/auth/refresh", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refreshToken }),
		})
			.then(async (response) => {
				// 401/403 means the refresh token itself is dead (revoked, expired,
				// deactivated user) - the session is genuinely over.
				if (response.status === 401 || response.status === 403) {
					this.logout();
					throw new Error("Token refresh rejected");
				}
				// Anything else (429 from the rate limiter, 5xx, proxy blip) says
				// nothing about the token's validity. Keep it and let the caller
				// retry, rather than dropping the user at the login screen.
				if (!response.ok) {
					throw new Error(`Token refresh unavailable (${response.status})`);
				}
				const data = (await response.json()) as LoginResponse;
				this.storeTokens(data);
				return data;
			})
			.finally(() => {
				refreshPromise = null;
			});

		return refreshPromise;
	},

	/**
	 * Authenticated fetch wrapper. Attaches Authorization header and
	 * automatically retries once with a refreshed token on 401.
	 */
	async authenticatedFetch(
		input: RequestInfo | URL,
		init?: RequestInit,
	): Promise<Response> {
		const accessToken = this.getAccessToken();
		const headers = new Headers(init?.headers);
		if (accessToken) {
			headers.set("Authorization", `Bearer ${accessToken}`);
		}

		let response = await fetch(input, { ...init, headers });

		if (response.status === 401) {
			try {
				await this.refreshTokens();
			} catch {
				return response;
			}

			const newToken = this.getAccessToken();
			headers.set("Authorization", `Bearer ${newToken}`);
			response = await fetch(input, { ...init, headers });
		}

		return response;
	},
};

export default AuthService;
